import { MoreHoriz } from "@mui/icons-material";
import {
  AspectRatio,
  Box,
  Card,
  CardContent,
  CardOverflow,
  Chip,
  Dropdown,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  Typography,
} from "@mui/joy";
import { useMatches, useMatchRoute } from "@tanstack/react-router";
import { ApiChallengeInline } from "griptonite-api-client/types";
import { ComponentProps } from "react";

import { useCurrentVenueQuery, useCurrentVenueUuid } from "@/hooks/venues";
import getThumbnailUrl from "@/utils/getThumbnailUrl";

import formatPeriod from "../utils/formatPeriod";
import { CardLink } from "./Links";
import { useModal } from "./modals/useModal";

const colorsMap = {
  ACTIVE: "#1F9A00",
  UPCOMING: "#0075FF",
  ENDED: "#FD5348",
};

export const getChallengeStatusLabel = (status: ApiChallengeInline["status"]) => {
  const label = {
    ACTIVE: "Active",
    UPCOMING: "Upcoming",
    ENDED: "Ended",
  }[status];
  return label;
};

export const ChallengeStatusChip = ({
  status,
  ...props
}: {
  status: ApiChallengeInline["status"];
} & ComponentProps<typeof Chip>) => {
  const label = getChallengeStatusLabel(status);
  return (
    <Chip
      sx={{ color: colorsMap[status], fontWeight: "bold", ...props.sx }}
      variant="plain"
      size="md"
      {...props}
    >
      {label}
    </Chip>
  );
};

export const ChallengeCard = ({ challenge }: { challenge?: ApiChallengeInline }) => {
  const currentVenueUuid = useCurrentVenueUuid();

  const { data: venue } = useCurrentVenueQuery();

  const isActive = useMatchRoute()({
    to: `/gym/$venueUuid/challenges/$challengeUuid`,
    params: {
      venueUuid: currentVenueUuid,
      challengeUuid: challenge?.uuid,
    },
    fuzzy: true,
  });
  const matches = useMatches();

  let tab: "profile" | "entries" | "tools" = "profile";
  const currentRoute = matches[matches.length - 1];
  if (currentRoute?.routeId.startsWith("/gym/$venueUuid/challenges/$challengeUuid/entries")) {
    tab = "entries";
  } else if (currentRoute?.routeId.startsWith("/gym/$venueUuid/challenges/$challengeUuid/tools")) {
    tab = "tools";
  }

  const { display: displayModal } = useModal();

  // const endsInFuture = challenge?.endTime ? isFuture(challenge.endTime) : true;
  const cardSx = {
    borderColor: isActive ? "primary.blue2" : "neutral.200",
    borderWidth: isActive ? "2px" : "1px",
    textDecoration: "none",
  };
  const content = (
    <>
      <CardOverflow>
        <AspectRatio ratio="2" objectFit="cover">
          {challenge?.coverImage ? (
            <img
              src={
                getThumbnailUrl(challenge.coverImage.url, {
                  size: 600,
                  mode: "resize",
                }) ?? undefined
              }
            />
          ) : null}
        </AspectRatio>
        {challenge ? (
          <Dropdown>
            <MenuButton
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                borderRadius: "100%",
              }}
              slots={{
                root: IconButton,
              }}
              slotProps={{
                root: {
                  size: "sm",
                  color: "neutral",
                  variant: "soft",
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <MoreHoriz />
            </MenuButton>
            <Menu size="sm">
              <MenuItem
                color="danger"
                onClick={(e) => {
                  displayModal("CONFIRM_DELETE_CHALLENGE", { challenge });
                  e.preventDefault();
                }}
              >
                Delete
              </MenuItem>
            </Menu>
          </Dropdown>
        ) : null}
      </CardOverflow>
      <CardContent sx={{ position: "relative" }}>
        {challenge && !challenge.venues.some((x) => x.uuid === currentVenueUuid) ? (
          <Box
            sx={{
              position: "absolute",
              top: 2,
              right: 8,
              display: "flex",
              backgroundColor: "neutral.100",
              borderRadius: 6,
              zIndex: 1,
              transform: "translateY(-100%)",
              background: "white",
              padding: 0.25,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                overflow: "hidden",
                borderRadius: 4,
                width: 40,
                height: 40,
              }}
            >
              <img
                src={challenge!.venues[0].logoUrl ?? undefined}
                alt={challenge!.venues[0].name}
                style={{ width: 40, height: 40 }}
              ></img>
            </Box>
          </Box>
        ) : null}
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography level="title-md" fontWeight="bold">
            {challenge?.name || "New challenge"}
          </Typography>
          <ChallengeStatusChip status={challenge?.status || "UPCOMING"} />
        </Box>
        <Typography textColor="neutral.500" level="body-sm">
          {challenge ? formatPeriod(challenge!, venue?.timezone || "UTC") : null}
        </Typography>
      </CardContent>
    </>
  );

  if (!challenge) {
    return <Card sx={cardSx}>{content}</Card>;
  }
  return (
    <CardLink
      data-uuid={challenge.uuid}
      from="/gym/$venueUuid"
      to={`/gym/$venueUuid/challenges/$challengeUuid/${tab}`}
      params={{ challengeUuid: challenge.uuid }}
      sx={cardSx}
    >
      {content}
    </CardLink>
  );
};
